import escapeRegExp from "lodash/escapeRegExp";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Config } from "../../../config/api";
import { useDebounce } from "../../../libs/component_utils";
import HttpConnect from "../../../libs/http_connect";
import AgenciesService from "../../../services/agencies";
import CampaignsService from "../../../services/campaign";
import T1Service from "../../../services/t1";

/**
 * Custom hook that provides common report form logic (copied and modified logic into here, may need some cleanup and refactoring)
 * @param {object} values - Form values
 * @param {Function} updateValues - Function to update form values which is sent to api
 * @param {string} type - Report type (new_to_brand, performance, site_transparency, geo, app_transparency)
 * @returns {object} - Form state and functions
 */
export const useReportForm = ({ values, updateValues, type }) => {
  const isNewToBrandReport = type === "new_to_brand";
  const services = useRef(
    new Map([
      ["t1", new T1Service()],
      ["agencies", new AgenciesService()],
      ["campaigns", new CampaignsService()],
    ]),
  );

  const agency = services.current.get("agencies").getSelectedAgency() || 0;
  const campaignsService = services.current.get("campaigns");

  /* Initial advertiser / campaign lists for empty search query: */
  const initialAdvertisers = useRef(null);
  const initialCampaigns = useRef(null);

  // State
  const [advertisersLoading, setAdvertisersLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [advertisers, setAdvertisers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignSearchQuery, setCampaignSearchQuery] = useState("");

  const handleStartDateChange = (date) => {
    // biome-ignore lint/style/useNamingConvention: <server prop>
    if (date) updateValues({ start_date: date });
  };

  const handleEndDateChange = (date) => {
    // biome-ignore lint/style/useNamingConvention: <server prop>
    if (date) updateValues({ end_date: date });
  };

  /**
   * Handle advertiser dropdown × button click.
   * If called directly, just reset advertisers.
   * @param {Event} e
   */
  const resetAdvertisers = (e) => {
    if (
      initialAdvertisers.current === null ||
      (e && !(e.target.tagName.toLowerCase() === "i" && e.target.classList.contains("clear")))
    ) {
      return;
    }

    HttpConnect.cancelRequest();

    setAdvertisersLoading(false);
    setCampaignsLoading(false);
    setAdvertisers(initialAdvertisers.current);
  };

  const resetSelectedCampaigns = () => {
    clearCampaignsSearchQuery();
    resetCampaigns();

    updateValues({
      // biome-ignore lint/style/useNamingConvention: <server prop>
      campaign_ids: [],
    });
  };

  const clearCampaignsSearchQuery = () => {
    setCampaignSearchQuery("");
  };

  const resetCampaigns = (_e) => {
    if (initialCampaigns.current === null) {
      return;
    }

    HttpConnect.cancelRequest();
    setAdvertisersLoading(false);
    setCampaignsLoading(false);
    const selectedCampaigns = campaigns.filter(
      ({ key }) => values.campaign_ids.includes(key) && !initialCampaigns.current.map(({ key }) => key).includes(key),
    );
    setCampaigns([...selectedCampaigns, ...initialCampaigns.current]);
  };

  // Attach reset handlers:
  // biome-ignore lint/correctness/useExhaustiveDependencies: <copied over, to be refactored, is it even needed?>
  useLayoutEffect(() => {
    const advertisersEl = document.querySelector("[data-id='advertisers']");
    const campaignsEl = document.querySelector("[data-id='campaigns']");

    if (advertisersEl) advertisersEl.addEventListener("click", resetAdvertisers);
    if (campaignsEl) campaignsEl.addEventListener("click", resetCampaigns);

    return () => {
      if (advertisersEl) advertisersEl.removeEventListener("click", resetAdvertisers);
      if (campaignsEl) campaignsEl.removeEventListener("click", resetCampaigns);
    };
  }, []);

  const performSearchAdvertisers = (query) => {
    const service = services.current.get("t1");

    const queryTrimmed = query.trim();
    if (queryTrimmed === "" && initialAdvertisers.current !== null) {
      return resetAdvertisers();
    }

    (async () => {
      HttpConnect.cancelRequest();
      setCampaignsLoading(false);

      setAdvertisersLoading(true);
      const response = await service.advertisers(agency, queryTrimmed);
      const formattedData = response.data.map(({ id, title }) => ({
        key: id,
        text: title,
        value: id,
      }));

      if (queryTrimmed === "" && initialAdvertisers.current === null) {
        initialAdvertisers.current = formattedData;
      }

      setAdvertisers(formattedData);

      if (response.status === "Error") {
        console.error("[performSearchAdvertisers]", response.error.message);
      }
      if (query === "" && response.data.length === 1) {
        // biome-ignore lint/style/useNamingConvention: <server prop>
        updateValues({ advertiser_id: response.data[0].id });
      }
      setAdvertisersLoading(false);
    })();
  };

  const performSearchAdvertisersDebounced = useDebounce(Config.search_debounce_delay, performSearchAdvertisers);

  const fetchCampaigns = async (query) => {
    const params = {};
    params.id_type = "advertiser";
    if (query) params.campaign_name = query;
    if (isNewToBrandReport) {
      params.new_to_brand = true;
    }

    setCampaignsLoading(true);
    const campaignsResp = await campaignsService.getCampaignsByType(values.advertiser_id, params);
    return campaignsResp;
  };

  const performSearchCampaigns = async (query) => {
    const queryTrimmed = query.trim();
    if (queryTrimmed === "" && initialCampaigns.current !== null) {
      return resetCampaigns();
    }

    try {
      if (!values.advertiser_id) return;
      setAdvertisersLoading(false);
      setCampaignsLoading(true);

      const campaignsResp = await fetchCampaigns(queryTrimmed);

      const nextCampaigns = campaignsResp.data.map(({ id, title }) => ({
        key: id,
        text: title,
        value: id,
      }));

      const selectedCampaigns = campaigns.filter(
        ({ key }) => values.campaign_ids.includes(key) && !nextCampaigns.map(({ key }) => key).includes(key),
      );

      if (queryTrimmed === "" && initialCampaigns.current === null) {
        initialCampaigns.current = nextCampaigns;
      }

      setCampaigns([...selectedCampaigns, ...nextCampaigns]);
    } catch (e) {
      console.error(e);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const performSearchCampaignsDebounced = useDebounce(Config.search_debounce_delay, performSearchCampaigns);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <copied over, to be refactored>
  useEffect(() => {
    initialCampaigns.current = null;
    clearCampaignsSearchQuery();
    setCampaigns([]);
    (async () => {
      await performSearchCampaigns("");
    })();
  }, [values.advertiser_id]);

  /**
   * initial loading advertisers list if standard reports
   * initial loading campaigns list if new to brand report
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: <run on mount>
  useEffect(() => {
    performSearchAdvertisers("");
  }, []);

  const trimmingSearch = (options, query) => {
    const re = new RegExp(escapeRegExp(query.trim()), "i");
    return options.filter((opt) => re.test(opt.text));
  };

  return {
    advertisers,
    campaigns,
    advertisersLoading,
    campaignsLoading,
    campaignSearchQuery,
    handleStartDateChange,
    handleEndDateChange,
    resetSelectedCampaigns,
    setCampaignSearchQuery,
    trimmingSearch,
    performSearchAdvertisersDebounced,
    performSearchCampaignsDebounced,
    setAdvertisers,
    initialAdvertisers,
    resetCampaigns,
  };
};
